import {
  createFileRoute,
  Link,
  Outlet,
  useCanGoBack,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type { AdminUserDetailResponse } from '@/lib/types';

function AdminUserDetailLayout() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? undefined, [searchStr]);

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const data = userQuery.data;
  const householdIds = useMemo(
    () => new Set((data?.households ?? []).map((h) => h.household_id)),
    [data?.households],
  );

  const base = `/admin/users/${userId}`;
  const pathTail = pathname.startsWith(base) ? pathname.slice(base.length) || '/' : '/';

  useEffect(() => {
    if (!data?.households?.length) return;
    const n = hid ? Number(hid) : NaN;
    if (hid && Number.isFinite(n) && householdIds.has(n)) return;

    const userBase = `/admin/users/${userId}`;
    const tail = pathTail === '/' ? '/' : pathTail;
    const payload =
      tail === '/' || tail === ''
        ? {
            to: '/admin/users/$userId' as const,
            params: { userId },
            search: { hid: String(data.households[0].household_id) },
            replace: true as const,
          }
        : tail === '/households'
          ? {
              to: '/admin/users/$userId/households' as const,
              params: { userId },
              search: { hid: String(data.households[0].household_id) },
              replace: true as const,
            }
          : tail === '/expenses'
            ? {
                to: '/admin/users/$userId/expenses' as const,
                params: { userId },
                search: { hid: String(data.households[0].household_id) },
                replace: true as const,
              }
            : tail === '/chat'
              ? {
                  to: '/admin/users/$userId/chat' as const,
                  params: { userId },
                  search: { hid: String(data.households[0].household_id) },
                  replace: true as const,
                }
              : tail === '/journal'
                ? {
                    to: '/admin/users/$userId/journal' as const,
                    params: { userId },
                    search: { hid: String(data.households[0].household_id) },
                    replace: true as const,
                  }
                : tail === '/audit'
                  ? {
                      to: '/admin/users/$userId/audit' as const,
                      params: { userId },
                      search: { hid: String(data.households[0].household_id) },
                      replace: true as const,
                    }
                  : tail === '/actions'
                    ? {
                        to: '/admin/users/$userId/actions' as const,
                        params: { userId },
                        search: { hid: String(data.households[0].household_id) },
                        replace: true as const,
                      }
                    : tail === '/tools'
                      ? {
                          to: '/admin/users/$userId/tools' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        }
                      : tail === '/exports'
                      ? {
                          to: '/admin/users/$userId/exports' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        }
                      : {
                          to: '/admin/users/$userId' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        };

    const timer = window.setTimeout(() => {
      if (!window.location.pathname.startsWith(userBase)) return;
      navigate(payload);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [data, hid, householdIds, navigate, pathTail, userId]);

  const tabClass = (suffix: string) => {
    const active =
      suffix === ''
        ? pathname === base || pathname === `${base}/`
        : pathname.startsWith(`${base}${suffix}`);
    return active
      ? 'border-primary text-primary font-semibold'
      : 'border-transparent text-gray-600 hover:text-gray-900';
  };

  const displayName =
    [data?.first_name, data?.last_name].filter(Boolean).join(' ') || `User #${id}`;

  const hasHouseholds = (data?.households.length ?? 0) > 0;
  const defaultHid =
    hasHouseholds && data ? String(data.households[0].household_id) : undefined;

  const tabSearch = (): { hid: string | undefined } => ({
    hid: defaultHid ? (hid ?? defaultHid) : undefined,
  });

  const handleBack = () => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: '/admin' });
  };

  return (
    <div className='space-y-4'>
      <button
        type='button'
        onClick={handleBack}
        className='inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm'
      >
        <FiArrowLeft className='w-4 h-4' />
        Back
      </button>

      {userQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : userQuery.isError ? (
        <p className='text-red-600'>Failed to load user.</p>
      ) : !data ? null : (
        <>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3'>
            <div>
              <h1 className='text-xl font-semibold text-gray-900'>{displayName}</h1>
              <p className='text-sm text-gray-500 mt-1'>
                User ID {data.id}
                {data.deleted_at ? (
                  <span className='ml-2 inline-flex px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold'>
                    Deleted
                  </span>
                ) : null}
              </p>
            </div>
            <div className='flex flex-wrap gap-2'>
              {data.subscribed ? (
                <span className='inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold'>
                  Subscribed{data.subscription_override ? ' (override)' : ''}
                </span>
              ) : (
                <span className='text-gray-500 text-xs'>Not subscribed</span>
              )}
              {data.has_push_token ? (
                <span className='inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold'>
                  Push token
                </span>
              ) : (
                <span className='inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs'>
                  No push token
                </span>
              )}
            </div>
          </div>

          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-sm text-gray-700 space-y-2'>
            <div className='flex flex-wrap gap-x-6 gap-y-1'>
              <span>
                <span className='text-gray-500'>Email:</span> {data.email ?? '—'}
              </span>
              <span>
                <span className='text-gray-500'>Phone:</span> {data.phone_number ?? '—'}
              </span>
              <span>
                <span className='text-gray-500'>Clerk:</span>{' '}
                <code className='text-xs bg-gray-100 px-1 rounded'>{data.clerk_id ?? '—'}</code>
              </span>
            </div>
            {hasHouseholds && data.households.length > 1 ? (
              <div className='pt-2 border-t border-gray-100'>
                <span className='text-xs font-semibold text-gray-600 mr-2'>Household:</span>
                <div className='inline-flex flex-wrap gap-2 mt-1'>
                  {data.households.map((h) => (
                    <Link
                      key={h.household_id}
                      to='.'
                      search={{ hid: String(h.household_id) }}
                      className={`px-2 py-1 rounded-md text-xs font-medium border ${
                        hid === String(h.household_id)
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      #{h.household_id}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <nav className='flex flex-wrap gap-2 border-b border-gray-200 pb-px'>
            <Link
              to='/admin/users/$userId'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('')}`}
            >
              Overview
            </Link>
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/households'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/households')}`}
              >
                Households
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/expenses'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/expenses')}`}
              >
                Expenses
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/chat'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/chat')}`}
              >
                Chat
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/journal'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/journal')}`}
              >
                Journal
              </Link>
            ) : null}
            <Link
              to='/admin/users/$userId/audit'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/audit')}`}
            >
              Audit
            </Link>
            <Link
              to='/admin/users/$userId/actions'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/actions')}`}
            >
              Actions
            </Link>
            <Link
              to='/admin/users/$userId/tools'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/tools')}`}
            >
              Tools
            </Link>
            <Link
              to='/admin/users/$userId/exports'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/exports')}`}
            >
              Exports
            </Link>
          </nav>

          <Outlet />
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserDetailLayout,
});
